<template>
  <component :is="embedded ? 'div' : 'v-main'">
    <component :is="embedded ? 'div' : 'v-container'" v-bind="embedded ? {} : { fluid: true }">
      <v-card :elevation="embedded ? 0 : 6" :rounded="embedded ? undefined : 'xxl'">
        <!-- Embedded (dialog) use gets its banner header from AppDialog instead. -->
        <v-card-title v-if="!embedded" class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon>
          </v-btn>
          Add Company Stock
        </v-card-title>
        <v-card-text>
          <v-row>
            <!-- Product Selection -->
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="selectedProduct"
                :items="filteredProducts"
                outlined
                item-text="name"
                item-value="id"
                prepend-inner-icon="mdi-package-variant"
                :error-messages="errors.product_id"
                @change="onProductChange"
                clearable
                :loading="isLoadingProducts"
                :no-data-text="
                  isLoadingProducts
                    ? 'Loading products...'
                    : 'No products with available variants'
                "
              >
                <template v-slot:label>Select Product <span style="color:#f44336;font-weight:bold;">*</span></template>
                <template v-slot:prepend-inner>
                  <v-icon color="primary">mdi-package-variant</v-icon>
                </template>
              </v-autocomplete>
            </v-col>

            <!-- Variant Selection -->
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="form.variant_id"
                :items="filteredVariants"
                outlined
                :item-text="formatVariantName"
                item-value="id"
                :error-messages="errors.variant_id"
                :disabled="!selectedProduct"
                clearable
                :no-data-text="
                  !selectedProduct
                    ? 'Select a product first'
                    : 'No available variants for this product'
                "
              >
                <template v-slot:label>Select Product Variant <span style="color:#f44336;font-weight:bold;">*</span></template>
                <template v-slot:prepend-inner>
                  <v-icon :color="selectedProduct ? 'primary' : 'grey'">
                    mdi-format-list-bulleted-square
                  </v-icon>
                </template>
              </v-autocomplete>
            </v-col>

            <!-- Quantity -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model.number="form.quantity"
                outlined
                type="number"
                min="1"
                :error-messages="errors.quantity"
                @input="form.quantity = parseInt(form.quantity) || ''"
                hint="Enter the stock quantity"
                persistent-hint
              >
                <template v-slot:label>Quantity <span style="color:#f44336;font-weight:bold;">*</span></template>
                <template v-slot:prepend-inner>
                  <v-icon color="primary">mdi-numeric</v-icon>
                </template>
              </v-text-field>
            </v-col>
            <!-- Selected Variant Details -->
            <v-col cols="12" v-if="selectedVariant">
              <v-card outlined class="pa-4 primary lighten-5">
                <h4 class="mb-3">Selected Variant Details:</h4>
                <v-row>
                  <v-col cols="6" sm="3">
                    <div class="mb-2">
                      <span class="grey--text text--darken-1">Color:</span>
                      <div class="text-h6 mt-1">{{ selectedVariant.color || "N/A" }}</div>
                    </div>
                  </v-col>
                  <v-col cols="6" sm="3">
                    <div class="mb-2">
                      <span class="grey--text text--darken-1">Size:</span>
                      <div class="text-h6 mt-1">{{ selectedVariant.size || "N/A" }}</div>
                    </div>
                  </v-col>
                  <v-col cols="6" sm="3">
                    <div class="mb-2">
                      <span class="grey--text text--darken-1">Price:</span>
                      <div class="text-h6 mt-1 primary--text">₹{{ selectedVariant.price || "0.00" }}</div>
                    </div>
                  </v-col>
                  <v-col cols="6" sm="3">
                    <div class="mb-2">
                      <span class="grey--text text--darken-1">Discount:</span>
                      <div class="text-h6 mt-1 success--text">{{ selectedVariant.discount_percentage || 0 }}%</div>
                    </div>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>

          <div class="mt-6">
            <v-btn text class="mr-2" @click="embedded ? $emit('close') : $router.back()">Cancel</v-btn>
            <v-btn
              rounded
              color="primary"
              :loading="isLoading"
              :disabled="isLoading"
              @click="save"
            >
              Save Company Stock
            </v-btn>
          </div>
        </v-card-text>
      </v-card>

      <!-- Success Dialog Box -->
      <v-dialog v-model="isSuccessDialog" max-width="500" persistent>
        <v-card rounded="lg">
          <v-card-text class="text-center pa-6">
            <v-icon color="success" size="80" class="mb-4">
              mdi-check-circle
            </v-icon>
            <h2 class="success--text mb-2">Success!</h2>
            <p class="text-h6 mb-6">Company Stock added successfully.</p>

            <v-row justify="center" class="mt-4">
              <v-col cols="12" sm="6">
                <v-btn
                  color="success"
                  block
                  rounded
                  @click="continueAdding"
                  class="mb-2 mb-sm-0"
                >
                  <v-icon left>mdi-plus</v-icon>
                  ADD MORE
                </v-btn>
              </v-col>
              <v-col cols="12" sm="6">
                <v-btn
                  color="primary"
                  block
                  rounded
                  @click="finishAdding"
                >
                  <v-icon left>mdi-check</v-icon>
                  DONE
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-dialog>
    </component>
  </component>
</template>

<script>
import axios from "axios";

export default {
  name: "CompanyStockCreatePage",
  props: {
    // When true, this form is rendered inside another page's dialog (e.g.
    // the Company Stocks list's inline "Add Company Stock") instead of its
    // own routed page - it emits `created`/`close` rather than navigating.
    embedded: { type: Boolean, default: false },
  },
  computed: {
    errors() {
      return this.$store.getters["errors/getErrors"] || {};
    },
    // Filter products to only show those with available variants
    filteredProducts() {
      if (!this.products || this.products.length === 0) return [];

      return this.products
        .filter((product) => {
          // Check if product has any variants
          if (
            !product.product_variants ||
            product.product_variants.length === 0
          ) {
            return false;
          }

          // Check if at least one variant is not already added
          const hasAvailableVariants = product.product_variants.some(
            (variant) => !this.existingVariantIds.includes(variant.id)
          );

          return hasAvailableVariants;
        })
        .map((product) => {
          // Count available variants for display
          const availableCount = product.product_variants.filter(
            (v) => !this.existingVariantIds.includes(v.id)
          ).length;
          const totalCount = product.product_variants.length;

          return {
            ...product,
            name: `${product.name} (${availableCount}/${totalCount} variants available)`,
          };
        });
    },

    // Filter variants to exclude already added ones
    filteredVariants() {
      if (!this.productVariants || this.productVariants.length === 0) return [];

      return this.productVariants.filter(
        (variant) => !this.existingVariantIds.includes(variant.id)
      );
    },
  },
  data() {
    return {
      form: {
        product_id: null,
        variant_id: null,
        quantity: "",
        is_active: 1,
      },
      selectedProduct: null,
      products: [],
      productVariants: [],
      selectedVariant: null,
      isLoading: false,
      isLoadingProducts: true, // Track loading state for products
      isSuccessDialog: false,
      existingCompanyStocks: [],
      existingVariantIds: [],
    };
  },
  created() {
    this.$store.dispatch("errors/setErrors", {});
    this.getExistingCompanyStocks();
    this.getProducts();
  },
  methods: {
    async getExistingCompanyStocks() {
      try {
        const response = await axios.get("/company_stocks");
        this.existingCompanyStocks = response.data.data;
        // Extract all existing variant IDs
        this.existingVariantIds = this.existingCompanyStocks.map(
          (stock) => stock.variant_id
        );
      } catch (error) {
        console.log("Error fetching existing company stocks:", error);
      }
    },
    async getProducts() {
      this.isLoadingProducts = true;
      try {
        const response = await axios.get("/products");
        this.products = response.data.data;
      } catch (error) {
        console.log("Error fetching products:", error);
      } finally {
        this.isLoadingProducts = false;
      }
    },
    onProductChange() {
      // Reset variant selection
      this.form.variant_id = null;
      this.productVariants = [];
      this.selectedVariant = null;
      // Clear any previous errors when product changes
      this.$store.dispatch("errors/setErrors", {});

      if (!this.selectedProduct) {
        this.form.product_id = null;
        return;
      }

      // Set the product_id in form
      this.form.product_id = this.selectedProduct;

      // Find the selected product from the original products array (not filtered)
      const product = this.products.find((p) => p.id === this.selectedProduct);
      if (product && product.product_variants) {
        // Set all variants, filtering will be done by the computed property
        this.productVariants = product.product_variants;
      }
    },
    formatVariantName(variant) {
      let name = [];
      if (variant.color) name.push(`Color: ${variant.color}`);
      if (variant.size) name.push(`Size: ${variant.size}`);
      if (variant.price) name.push(`₹${variant.price}`);

      const formattedName = name.length > 0 ? name.join(" | ") : "Variant";

      // Add indicator if variant is already added (though it shouldn't appear in filtered list)
      if (this.existingVariantIds.includes(variant.id)) {
        return `${formattedName} (Already added)`;
      }

      return formattedName;
    },
    finishAdding() {
      this.isSuccessDialog = false;
      if (this.embedded) {
        this.$emit("created");
        this.$emit("close");
      } else {
        this.$router.push("/company-stocks");
      }
    },
    continueAdding() {
      // Close dialog and reset form for adding more items
      this.isSuccessDialog = false;
      this.form = {
        variant_id: null,
        quantity: "",
        is_active: 1,
      };
      this.selectedProduct = null;
      this.productVariants = [];
      this.selectedVariant = null;
      this.$store.dispatch("errors/setErrors", {});
    },
    async save() {
      try {
        this.isLoading = true;
        // Clear any previous errors
        this.$store.dispatch("errors/setErrors", {});

        // Client-side validation
        let validationErrors = {};
        if (!this.form.variant_id) {
          validationErrors.variant_id = ["Product variant is required"];
        }
        if (!this.form.quantity || this.form.quantity <= 0) {
          validationErrors.quantity = ["Quantity must be greater than 0"];
        }
        if (!this.form.product_id) {
          validationErrors.product_id = ["Product is required"];
        }

        if (Object.keys(validationErrors).length > 0) {
          this.$store.dispatch("errors/setErrors", validationErrors);
          this.isLoading = false;
          return;
        }

        const response = await axios.post("/company_stocks", this.form);

        if (response.data.success) {
          // Refresh existing company stocks to update filters
          await this.getExistingCompanyStocks();
          this.isSuccessDialog = true;
          // Clear errors on success
          this.$store.dispatch("errors/setErrors", {});
        }
      } catch (error) {
        console.log("Save error:", error);
        if (error.response && error.response.data) {
          // Handle Laravel validation errors (422 status)
          if (error.response.status === 422 && error.response.data.errors) {
            this.$store.dispatch("errors/setErrors", error.response.data.errors);
          } else if (error.response.data.message) {
            // Handle general error message
            if (this.$toast) this.$toast.error(error.response.data.message);
          } else {
            if (this.$toast) this.$toast.error("Failed to save company stock");
          }
        } else {
          if (this.$toast) this.$toast.error("Failed to save company stock");
        }
      } finally {
        this.isLoading = false;
      }
    },
  },
  watch: {
    "form.variant_id": function (newVal) {
      if (newVal) {
        this.selectedVariant = this.productVariants.find(
          (v) => v.id === newVal
        );
      } else {
        this.selectedVariant = null;
      }
    },
  },
};
</script>

<style scoped></style>
